import type { TExample } from '@atlas/uicorn/preview/modules';
import React, { useContext } from 'react';
import styled from 'styled-components';
import { Input as AntInput, InputProps as TAntInputProps, InputRef as TAntInputRef } from 'antd';
import type { TextAreaProps as TAntTextAreaProps } from 'antd/lib/input/TextArea';
import cn from 'classnames';
import { FormContext } from '@atlas/uicorn/lib/Control/Form';

const Input = React.memo(
    React.forwardRef<TInputRef, TInputProps>(function Input(props, ref): React.ReactElement {
        const { disabled: contextDisabled } = useContext(FormContext);

        const disabled = props.disabled ?? contextDisabled;

        if (props.type === 'multiline') {
            const { type: _type, className, autoSize = defaultAutoSize, prefix, suffix, hideError, ...rest } = props;
            return (
                <SMultilineInputContainer>
                    {prefix && <span className="ant-input-prefix">{prefix}</span>}
                    <SInputMultiline
                        {...rest}
                        className={cn(className, { ['atlas-disabled']: disabled })}
                        autoSize={autoSize}
                        status={hideError ? ('ok' as any) : undefined}
                        ref={ref}
                    />
                    {suffix && <span className="ant-input-suffix">{suffix}</span>}
                </SMultilineInputContainer>
            );
        }

        const { hideError, ...inputProps } = props;

        return <SInput {...inputProps} status={hideError ? ('ok' as any) : undefined} ref={ref} />;
    }),
);

export default Input;

// #region Helpers

const defaultAutoSize = {
    minRows: 2,
    maxRows: 6,
};

// #endregion

// #region Styles

export const SMultilineInputContainer = styled.div`
    ${(p) => p.theme.patch.input}

    display: flex;
    align-items: flex-start;

    .ant-input,
    .ant-input-textarea {
        padding: 0;
        border: none;
        border-radius: unset;
        width: 100%;
    }

    .ant-input-prefix,
    .ant-input-suffix {
        line-height: 1.5715;
    }

    /* Move focused state logic from input to container */
    .ant-input:is(:not(:disabled):not([data-disabled='true'])):focus,
    .ant-input:is(:not(:disabled):not([data-disabled='true']))[data-focus='true'] {
        border: none;
        box-shadow: none;
    }

    &:has(.ant-input:is(:not(:disabled):not([data-disabled='true'])):focus),
    &:has(.ant-input:is(:not(:disabled):not([data-disabled='true']))[data-focus='true']) {
        border-width: 0.1rem;
        border-color: ${(p) => p.theme.color.primary};
        box-shadow: 0rem 0rem 0rem 0.2rem rgba(0, 0, 255, 0.1);
    }
`;

export const SInput = styled(AntInput)`
    ${(p) => p.theme.patch.input}

    .ant-input {
        background: transparent;
        border-radius: unset;
    }
`;

export const SInputMultiline = styled(AntInput.TextArea)`
    ${(p) => p.theme.patch.input}

    &.ant-input-textarea .ant-input {
        border: none;
        padding: 0;

        &:focus {
            outline: none;
            box-shadow: none;
        }
    }

    .ant-input,
    .ant-input-affix-wrapper {
        background: transparent;
        border-radius: unset;
    }
`;

// #endregion

// #region Types

export type TInputProps = Pick<
    TAntInputProps,
    | 'id'
    | 'className'
    | 'style'
    | 'defaultValue'
    | 'value'
    | 'name'
    | 'placeholder'
    | 'required'
    | 'disabled'
    | 'readOnly'
    | 'maxLength'
    | 'autoFocus'
> & {
    state?: 'default' | 'warn' | 'error';
    showCount?: boolean;
    allowClear?: boolean;
    hideError?: boolean;
} & (
        | ({
              type?: 'text' | 'password' | 'email' | 'tel' | 'url';
              onChange?: TAntInputProps['onChange'];
              onFocus?: TAntInputProps['onFocus'];
              onBlur?: TAntInputProps['onBlur'];
              onInput?: TAntInputProps['onInput'];
              onClick?: TAntInputProps['onClick'];
              onKeyDown?: TAntInputProps['onKeyDown'];
              onPressEnter?: TAntInputProps['onPressEnter'];
          } & Pick<TAntInputProps, 'prefix' | 'suffix'>)
        | ({
              type: 'multiline';
              autoSize?: TAntTextAreaProps['autoSize'];
              onChange?: TAntTextAreaProps['onChange'];
              onFocus?: TAntTextAreaProps['onFocus'];
              onBlur?: TAntTextAreaProps['onBlur'];
              onInput?: TAntTextAreaProps['onInput'];
              onClick?: TAntTextAreaProps['onClick'];
              onKeyDown?: TAntTextAreaProps['onKeyDown'];
              onPressEnter?: TAntTextAreaProps['onPressEnter'];
          } & Pick<TAntInputProps, 'prefix' | 'suffix'>)
    );

export type TInputRef = TAntInputRef;

// #endregion

// #region Examples

export const Example: TExample = {
    render(props) {
        return (
            <Input
                type={props.type}
                placeholder={props.placeholder}
                allowClear={props.allowClear}
                disabled={props.disabled}
                readOnly={props.readOnly}
                showCount={props.showCount}
                maxLength={props.maxLength}
                prefix={props.prefix}
                suffix={props.suffix}
                onChange={props.onChange}
            />
        );
    },
    controls: {
        type: ['text', 'multiline', 'password', 'email', 'tel', 'url'],
        state: ['default', 'warn', 'error'],
        placeholder: 'string',
        allowClear: 'boolean',
        disabled: 'boolean',
        readOnly: 'boolean',
        showCount: 'boolean',
        maxLength: 'number',
        prefix: { type: 'string', connected: false },
        suffix: { type: 'string', connected: false },
    },
    handlers: {
        onChange: 'console',
    },
    figma: 'https://www.figma.com/file/fRYzY91MBgJhjJwO17emBQ/Design-System----2.0?type=design&node-id=6-891&mode=design&t=dygdJrYUTAzsGhAY-0',
};

// #endregion

// #region Tests

if (import.meta.vitest) {
    const { describe, it, expect } = import.meta.vitest;
    describe('Input', async () => {
        const testModule = '@test';
        const { render } = await import(/* @vite-ignore */ testModule);

        it('matches snapshot', () => {
            const result = render(<Example.render />);
            expect(result.container).toMatchSnapshot();
        });
    });
}

// #endregion
